/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { FunctionComponent, useMemo } from 'react';
import { Route, Switch, Redirect, useParams } from 'react-router-dom';
import { Constants } from '@pwc/pwc-deals-web-common';
import authorizationService from 'common/services/security/authorizationService';
import EnableForProjectState from 'features/security/EnableForProjectState';
import ProjectPermissionRoute from 'features/security/ProjectPermissionRoute';
import { Loader } from '@pwc/pwc-deals-ui';
import { useSelector } from 'react-redux';
import {
  getProjectIsLoaded,
  getProjectLoading,
  isStructuredV2Enabled,
} from 'features/project/projectSlice';
import StructuredAnalysisMFE from './StructuredAnalysisMFE';

const StructuredAnalysisMFERoutingWrapper = ({
  isFlagStructuredV2Enabled,
  redirectPath,
}: {
  isFlagStructuredV2Enabled: boolean;
  redirectPath: string;
}) => {
  if (isFlagStructuredV2Enabled) {
    return <StructuredAnalysisMFE />;
  }
  return <Redirect to={redirectPath} />;
};

const StructuredAnalysisRouter: FunctionComponent = () => {
  const { projectId } = useParams<{
    projectId: string;
  }>();
  const isLoading = useSelector(getProjectLoading);
  const isLoaded = useSelector(getProjectIsLoaded);
  const enabledStructuredV2Flag = useSelector(isStructuredV2Enabled);
  const isFlagStructuredV2Enabled =
    !isLoading && isLoaded && enabledStructuredV2Flag;

  const areSettingsLoaded = !isLoading && isLoaded;
  const isAuthorized = (isProjectDisabled: boolean) => () =>
    !isProjectDisabled && authorizationService.hasValidToken();

  const redirectPath = useMemo(() => {
    const path = window.location.pathname;
    const isReportPreviewPage = path.includes('/report-preview/');

    const isStructuredURL = path.includes('/template-analyses/');

    if (isReportPreviewPage) {
      let pathSwapped = path.replace('/template-analyses/', '/analyses/');
      pathSwapped = pathSwapped.replace(
        '/report-preview/latest',
        '/report-preview/ANALYSIS/latest?isUnStructured=false'
      );
      return pathSwapped;
    }
    if (isStructuredURL) {
      return `/projects/${projectId}/analyses/add-type`;
    }
    return path;
  }, [projectId]);

  if (!authorizationService.hasValidToken()) {
    return <Redirect to="/" />;
  }

  if (!areSettingsLoaded) return <Loader />;

  return (
    <EnableForProjectState projectId={projectId}>
      {/* eslint-disable-next-line react/no-unused-prop-types */}
      {({ isProjectDisabled }: { isProjectDisabled: boolean }) => (
        <Switch>
          <ProjectPermissionRoute
            permissions={[Constants.USER_PERMISSIONS.CONTRIBUTE]}
            isAuthorized={isAuthorized(isProjectDisabled)}
            path="/projects/:projectId/template-analyses/:contentThreadId/edit"
            component={StructuredAnalysisMFERoutingWrapper}
            componentProps={{ isFlagStructuredV2Enabled, redirectPath }}
            fallbackRoute={
              isProjectDisabled ? `/projects/${projectId}/analyses/` : '/'
            }
          />
          <Route path="/projects/:projectId/template-analyses/:contentThreadId/view/:version">
            <StructuredAnalysisMFERoutingWrapper
              isFlagStructuredV2Enabled={isFlagStructuredV2Enabled}
              redirectPath={redirectPath}
            />
          </Route>
          <Route path="/projects/:projectId/template-analyses/:contentThreadId/report-preview/:version">
            <StructuredAnalysisMFERoutingWrapper
              isFlagStructuredV2Enabled={isFlagStructuredV2Enabled}
              redirectPath={redirectPath}
            />
          </Route>
          <Route path="/projects/:projectId/template-analyses">
            <Redirect to={`/projects/${projectId}/analyses/`} />
          </Route>
        </Switch>
      )}
    </EnableForProjectState>
  );
};

export default StructuredAnalysisRouter;
